import type { FC, ComponentType, ComponentProps } from 'react';
import React, { useRef, useEffect, useMemo } from 'react';
import type { MessageDescriptor } from 'react-intl-next';
import { defineMessages } from 'react-intl-next';

import EditIcon from '@atlaskit/icon/glyph/edit';
import LikeIcon from '@atlaskit/icon/glyph/like';
import CommentIcon from '@atlaskit/icon/glyph/comment';
import ClockIcon from '@atlaskit/icon/glyph/emoji/frequent';

import { useSessionData } from '@confluence/session-data';
import { NarrowPageCardView } from '@confluence/entity-view';

import type { HomeRecentActivitiesQuery_activities_myActivities_all_edges_node as RecentActivities } from '../Recent/__types__/HomeRecentActivitiesQuery';
import { transformRecentToPageCard } from '../pageCardTransformers';

import type { HomeRecentWorkQuery_activities_myActivities_all_edges_node as RecentWork } from './__types__/HomeRecentWorkQuery';
import { ActivityEventType } from './__types__/HomeRecentWorkQuery';

const i18n = defineMessages({
	page: {
		id: 'home.views.overview.recent-work.page.label',
		defaultMessage: 'Page',
		description: 'Label for page icon. Denotes this content is a page.',
	},
	blogpost: {
		id: 'home.views.overview.recent-work.blog.label',
		defaultMessage: 'Blog',
		description: 'Label for blog icon. Denotes this content is a blog.',
	},
	whiteboard: {
		id: 'home.views.overview.recent-work.whiteboard.label',
		defaultMessage: 'Whiteboard',
		description: 'Label for whiteboard icon. Denotes this content is a whiteboard.',
	},
	database: {
		id: 'home.views.overview.recent-work.database.label',
		defaultMessage: 'Database',
		description: 'Label for database icon. Denotes this content is a database.',
	},
	embed: {
		id: 'home.views.overview.recent-work.smart-link.label',
		defaultMessage: 'Smart Link',
		description: 'Label for the smart link icon. Denotes this content is a smart link.',
	},
	edited: {
		id: 'home.views.overview.recent-work.action.edited',
		defaultMessage: 'You edited {timeAgo}',
		description:
			"When you last edited the page. 'timeAgo' is of the form `1 hour ago` or a date `September 15, 2020` (in English)",
	},
	visited: {
		id: 'home.views.overview.recent-work.action.visited',
		defaultMessage: 'You visited {timeAgo}',
		description:
			"When you last visited the page. 'timeAgo' is of the form `1 hour ago` or a date `September 15, 2020` (in English)",
	},
	updated: {
		id: 'home.views.overview.recent-work.action.updated',
		defaultMessage: 'You updated {timeAgo}',
		description:
			"When you last modified the page. 'timeAgo' is of the form `1 hour ago` or a date `September 15, 2020` (in English)",
	},
	created: {
		id: 'home.views.overview.recent-work.action.created',
		defaultMessage: 'You created {timeAgo}',
		description:
			"When you created the page. 'timeAgo' is of the form `1 hour ago` or a date `September 15, 2020` (in English)",
	},
	liked: {
		id: 'home.views.overview.recent-work.action.liked',
		defaultMessage: 'You liked {timeAgo}',
		description:
			"When you liked the page. 'timeAgo' is of the form `1 hour ago` or a date `September 15, 2020` (in English)",
	},
	commented: {
		id: 'home.views.overview.recent-work.action.commented',
		defaultMessage: 'You commented {timeAgo}',
		description:
			"When you commented on the page. 'timeAgo' is of the form `1 hour ago` or a date `September 15, 2020` (in English)",
	},
	unpublished: {
		id: 'home.views.overview.recent-work.lozenge.unpublished',
		defaultMessage: 'unpublished changes',
		description: 'Label on a page that has changes which have not yet been published.',
	},
	draft: {
		id: 'home.views.overview.recent-work.lozenge.draft',
		defaultMessage: 'draft',
		description: 'Label on a page that is a draft',
	},
	editedShort: {
		id: 'home.views.overview.recent-work.action.edited.short',
		defaultMessage: 'Edited',
		description:
			'When you last edited the page. Timestamp is of the form `1 hour ago` or a date `September 15, 2020` (in English)',
	},
	visitedShort: {
		id: 'home.views.overview.recent-work.action.visited.short',
		defaultMessage: 'Visited',
		description:
			'When you last visited the page. Timestamp is of the form `1 hour ago` or a date `September 15, 2020` (in English)',
	},
	createdShort: {
		id: 'home.views.overview.recent-work.action.created.short',
		defaultMessage: 'Created',
		description:
			'When you created the page. Timestamp is of the form `1 hour ago` or a date `September 15, 2020` (in English)',
	},
	likedShort: {
		id: 'home.views.overview.recent-work.action.liked.short',
		defaultMessage: 'Liked',
		description:
			'When you liked the page. Timestamp is of the form `1 hour ago` or a date `September 15, 2020` (in English)',
	},
	commentedShort: {
		id: 'home.views.overview.recent-work.action.commented.short',
		defaultMessage: 'Commented',
		description:
			'When you commented on the page. Timestamp is of the form `1 hour ago` or a date `September 15, 2020` (in English)',
	},
});

const ActionIconStrings: Record<
	ActivityEventType,
	[ComponentType<ComponentProps<typeof ClockIcon>>, MessageDescriptor, MessageDescriptor]
> = {
	VIEWED: [ClockIcon, i18n.visited, i18n.visitedShort],
	EDITED: [EditIcon, i18n.edited, i18n.editedShort],
	UPDATED: [EditIcon, i18n.edited, i18n.editedShort],
	CREATED: [EditIcon, i18n.created, i18n.createdShort],
	PUBLISHED: [EditIcon, i18n.edited, i18n.editedShort],
	LIKED: [LikeIcon, i18n.liked, i18n.likedShort],
	COMMENTED: [CommentIcon, i18n.commented, i18n.commentedShort],
	// these shouldn't be possible for Confluence but are listed for completion
	ASSIGNED: [ClockIcon, i18n.visited, i18n.visitedShort],
	UNASSIGNED: [ClockIcon, i18n.visited, i18n.visitedShort],
	TRANSITIONED: [ClockIcon, i18n.visited, i18n.visitedShort],
};

type RecentListItemProps = {
	recent: RecentActivities | RecentWork;
	index: number;
	onItemClick: (
		index: number,
		action: string,
		status: string | undefined,
		contentId: string,
		spaceKey: string,
		tenantId: string,
		objectType: string,
	) => void;
	onItemSpaceClick: (index: number) => void;
	shouldAutofocus?: boolean;
};

export const RecentListItem: FC<RecentListItemProps> = ({
	recent,
	index,
	onItemClick,
	onItemSpaceClick,
	shouldAutofocus,
}) => {
	const { cloudId } = useSessionData();

	const isSpaceAliasFFEnabled = true;

	const page = useMemo(
		() => transformRecentToPageCard(recent as RecentActivities, '', isSpaceAliasFFEnabled),
		[recent, isSpaceAliasFFEnabled],
	);
	const action = recent.object?.events?.[0].eventType || ActivityEventType.VIEWED;
	const { status, id: contentId, type } = page;
	const spaceKey = page.space.spaceKey;

	const linkRef = useRef<HTMLAnchorElement>(null);

	useEffect(() => {
		if (shouldAutofocus && linkRef.current) {
			linkRef.current.focus();
		}
	}, [shouldAutofocus, linkRef]);

	return (
		<NarrowPageCardView
			key={index}
			recent={recent}
			lastActionI18n={ActionIconStrings[action][2]}
			onClick={() => onItemClick(index, action, status, contentId, spaceKey, cloudId, type)}
			onSpaceClick={() => onItemSpaceClick(index)}
		/>
	);
};
